import type { SpotSliceView, SpotView } from "@auto-pools/core";
import { decimal } from "@auto-pools/core";
import {
  formatAgo,
  formatMoney,
  formatSignedPercent,
  formatToken,
  formatWhen,
  signTone,
} from "../../lib/format";
import { spotCoinCoverage, spotCoverageText } from "./spotPresentation";

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** The weekday a slice reviews on, read off its own `nextReviewAt` rather than guessed from `k`. */
function sliceWeekday(slice: SpotSliceView): string {
  const date = new Date(slice.nextReviewAt);
  return Number.isFinite(date.getTime())
    ? WEEKDAY[date.getUTCDay()]
    : `Slice ${String(slice.k + 1)}`;
}

/**
 * A half's value: the exact sum of its slices' values. Null for an empty list — slices are empty
 * until the run has read the subaccount, and an unread balance is not $0.00 — or for any slice
 * without a value.
 */
function sumValue(slices: SpotSliceView[]): string | null {
  if (slices.length === 0) return null;
  let total = decimal("0");
  for (const slice of slices) {
    if (slice.value === null) return null;
    total = total.add(decimal(slice.value));
  }
  return total.toString();
}

/** Why a half has no value yet: the subaccount is being read, or has not been read. */
function unreadReason(spot: SpotView): string {
  return spot.readingSubaccount ? "reading the subaccount" : "not read yet";
}

/** A half's value, or "—" with the reason it is not known. */
function halfValue(spot: SpotView, value: string | null): string {
  if (value !== null) return formatMoney(value);
  return spot.slices.length === 0 ? `— · ${unreadReason(spot)}` : "—";
}

/** "Where the money sits" tree (design 51): a sum of already-provided per-slice values only. */
function MoneySitsTree({ spot }: { spot: SpotView }) {
  const calm = spot.slices.filter((s) => s.half === "calm");
  const season = spot.slices.filter((s) => s.half === "season");
  const calmValue = sumValue(calm);
  const seasonValue = sumValue(season);
  return (
    <div className="spot-money-tree">
      <div className="spot-money-tree-row spot-money-tree-total">
        <span className="num">{spot.card.value === null ? "—" : formatMoney(spot.card.value)}</span>
        <span>everything on the subaccount</span>
      </div>
      <div className="spot-money-tree-row">
        <span className="num">{halfValue(spot, calmValue)}</span>
        <span className="spot-money-tree-dot calm" aria-hidden="true" />
        <span>calm half · seven slices, one per weekday</span>
      </div>
      <div className="spot-money-tree-row">
        <span className="num">{halfValue(spot, seasonValue)}</span>
        <span className="spot-money-tree-dot season" aria-hidden="true" />
        <span>season half · BTC when the season is off, USDT when BTC is below its average</span>
      </div>
      <p className="spot-money-tree-note">
        Slices are never evened against each other — they drift apart on purpose. Once a month only
        the two halves are brought back to 50/50.
      </p>
    </div>
  );
}

/** "Why the money is where it is" (design 51): the three alt-season conditions plus the verdict. */
function ConditionsTable({ spot }: { spot: SpotView }) {
  const c = spot.conditions;
  if (c === null)
    return (
      <p className="spot-expanded-unavailable">
        — · the newest daily decision is not available yet
      </p>
    );
  const btcDelta =
    c.btcClose !== null && c.sma100 !== null
      ? (Number(c.btcClose) / Number(c.sma100) - 1) * 100
      : null;
  const rows: {
    ok: boolean | null;
    condition: string;
    now: string;
    needs: string;
    decides: string;
  }[] = [
    {
      ok: c.btcOn,
      condition: "BTC closed above its 100-day average",
      now: btcDelta === null ? "—" : `${btcDelta >= 0 ? "+" : ""}${btcDelta.toFixed(1)} %`,
      needs: "above 0",
      decides: "there are coins at all",
    },
    {
      ok: c.breadth === null ? null : c.breadth >= c.breadthMin,
      condition: "Coins of the top thirty above their 50-day average",
      now: c.breadth === null ? "—" : `${(c.breadth * 100).toFixed(0)} %`,
      needs: `${(c.breadthMin * 100).toFixed(0)} % or more`,
      decides: "alt season · condition 1 of 3",
    },
    {
      ok: c.med30 === null ? null : c.med30 >= c.med30Min,
      condition: "Median 30-day growth of the top thirty",
      now: c.med30 === null ? "—" : `${c.med30 >= 0 ? "+" : ""}${(c.med30 * 100).toFixed(1)} %`,
      needs: `+${(c.med30Min * 100).toFixed(0)} % or more`,
      decides: "alt season · condition 2 of 3",
    },
    {
      ok: c.btcOn,
      condition: "BTC above its 100-day average",
      now: btcDelta === null ? "—" : `${btcDelta >= 0 ? "+" : ""}${btcDelta.toFixed(1)} %`,
      needs: "above 0",
      decides: "alt season · condition 3 of 3",
    },
  ];
  return (
    <div className="spot-conditions-table">
      {rows.map((row) => (
        <div className="spot-conditions-row" key={row.condition}>
          <span
            className={`status-dot ${row.ok === null ? "" : row.ok ? "good" : "bad"}`}
            aria-hidden="true"
          />
          <span className="spot-conditions-condition">{row.condition}</span>
          <span className="num spot-conditions-now">{row.now}</span>
          <span className="num spot-conditions-needs">{row.needs}</span>
          <span className="spot-conditions-decides">{row.decides}</span>
        </div>
      ))}
      <div className="spot-conditions-verdict">
        {c.alt
          ? "All three alt-season conditions hold, so the season half is in coins rather than in BTC."
          : "Not every alt-season condition holds, so the season half sits in BTC (or USDT if BTC is below its average) rather than in coins."}
      </div>
    </div>
  );
}

/** "Since bought" cell: the server's percent signed and colored; unavailable is "—" without a tone. */
function SinceBought({ percent }: { percent: string | null | undefined }) {
  const tone = typeof percent === "string" ? signTone(percent) : "";
  const toneClass = tone === "positive" || tone === "negative" ? ` ${tone}` : "";
  return (
    <span className={`num spot-holdings-since${toneClass}`}>
      <span className="spot-holdings-label">Since bought</span>
      {typeof percent === "string" ? formatSignedPercent(percent) : "—"}
    </span>
  );
}

/**
 * Holdings added up across slices (design 51): coin, half, slice coverage, quantity, value and the
 * price move since bought.
 */
function HoldingsTable({ spot }: { spot: SpotView }) {
  const total = spot.card.value;
  return (
    <div className="spot-holdings-table">
      <div className="spot-holdings-row spot-holdings-head">
        <span>Coin</span>
        <span>Half</span>
        <span>Slices</span>
        <span>Holding</span>
        <span>Weight</span>
        <span>Value</span>
        <span>Since bought</span>
      </div>
      {spot.coins.map((coin) => {
        const coverage = spotCoverageText(spotCoinCoverage(spot, coin.coin));
        return (
          <div className="spot-holdings-row" key={coin.coin}>
            <span>{coin.coin}</span>
            <span className="spot-holdings-half">{coverage.half}</span>
            <span className="num">{coverage.slices}</span>
            <span className="num">{formatToken(coin.qty)}</span>
            <span className="num">
              {coin.value === null || total === null || Number(total) === 0
                ? "—"
                : `${((Number(coin.value) / Number(total)) * 100).toFixed(1)} %`}
            </span>
            <span className="num">{coin.value === null ? "—" : formatMoney(coin.value)}</span>
            <SinceBought percent={coin.sinceBoughtPercent} />
          </div>
        );
      })}
      <div className="spot-holdings-row spot-holdings-foot">
        <span>USDT</span>
        <span>free</span>
        <span />
        <span />
        <span />
        <span className="num">
          {spot.card.freeUsdt === null ? "—" : formatMoney(spot.card.freeUsdt)}
        </span>
        <span />
      </div>
      {spot.coins.length > 0 && (
        <p className="spot-holdings-note">
          Since bought — price now (best bid) against the average price we paid for what is held,
          before fees.
        </p>
      )}
      {spot.foreign.length > 0 && (
        <p className="spot-holdings-note">
          Not bought by the engine, sold into USDT at the next decision:{" "}
          {spot.foreign.map((f) => `${formatToken(f.qty)} ${f.coin}`).join(", ")}.
        </p>
      )}
      {spot.unmanaged.length > 0 && (
        <p className="spot-holdings-note">
          Not managed · cannot be sold:{" "}
          {spot.unmanaged.map((u) => `${formatToken(u.qty)} ${u.coin} (${u.reason})`).join(", ")}.
        </p>
      )}
    </div>
  );
}

/** The fourteen slices (design 51 "the seven slices"), one row per half/weekday. */
function SlicesTable({ spot }: { spot: SpotView }) {
  if (spot.slices.length === 0)
    return <p className="spot-expanded-unavailable">— · the run has not read the subaccount yet</p>;
  const sorted = [...spot.slices].sort((a, b) =>
    a.half === b.half ? a.k - b.k : a.half === "calm" ? -1 : 1,
  );
  return (
    <div className="spot-slices-table">
      <div className="spot-slices-row spot-slices-head">
        <span>Half</span>
        <span>Day</span>
        <span>Value</span>
        <span>Cash</span>
        <span>Coins it holds</span>
        <span>Next review</span>
      </div>
      {sorted.map((slice) => (
        <div className="spot-slices-row" key={`${slice.half}-${String(slice.k)}`}>
          <span className="spot-holdings-half">{slice.half}</span>
          <span>{sliceWeekday(slice)}</span>
          <span className="num">{slice.value === null ? "—" : formatMoney(slice.value)}</span>
          <span className="num">{formatMoney(slice.cash)}</span>
          <span className="num spot-slices-coins">
            {slice.holdings.length === 0
              ? "USDT only"
              : slice.holdings.map((h) => h.coin).join(" · ")}
            {slice.mode && <span className="spot-slices-mode"> · mode {slice.mode}</span>}
          </span>
          <span className="num">{formatWhen(slice.nextReviewAt)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The chevron body (design 51): tonight's own per-coin plan is not in `SpotView` (the server
 * exposes targets/picks per decision, not a rendered "sell/trim/buy" line with a reason per coin),
 * so that part shows an explicit unavailable note instead of a guess — see the Phase 6 handoff.
 */
/**
 * The market-derived values (value at bid, Exit now, vs BTC HODL) come from a snapshot refreshed
 * once a minute and dropped by the server after 5 min; past two refreshes they are stale. The
 * status poll's own time is shown apart: it says nothing about how old the prices are.
 */
const PRICES_STALE_MS = 2 * 60_000;
function PricesNote({ spot }: { spot: SpotView }) {
  const status = `status ${formatAgo(spot.observedAt)}`;
  const asOf = spot.card.marketAsOf;
  const stale = asOf === null || Date.now() - Date.parse(asOf) > PRICES_STALE_MS;
  const prices =
    asOf === null ? "no live prices" : `prices ${formatAgo(asOf)}${stale ? " · stale" : ""}`;
  return (
    <span className={`spot-expanded-footer-note${stale ? " warn" : ""}`}>
      {`${prices} · ${status}`}
    </span>
  );
}

export function SpotCardExpanded({
  spot,
  onOpenJournal,
}: {
  spot: SpotView;
  onOpenJournal?: () => void;
}) {
  return (
    <div className="spot-expanded-body">
      <div className="spot-expanded-columns">
        <div className="spot-expanded-column">
          <h4 className="spot-expanded-heading">Tonight</h4>
          <p className="spot-expanded-unavailable">
            — · the per-coin plan for tonight's review is not available from the server yet; the
            holdings and conditions below reflect the last completed decision.
          </p>
          <h4 className="spot-expanded-heading">Where the money sits</h4>
          <MoneySitsTree spot={spot} />
        </div>
        <div className="spot-expanded-column">
          <div className="spot-expanded-heading-row">
            <h4 className="spot-expanded-heading">What is held now · all slices added up</h4>
            <span className="num spot-expanded-heading-note">
              calm {halfValue(spot, sumValue(spot.slices.filter((s) => s.half === "calm")))} ·
              season {halfValue(spot, sumValue(spot.slices.filter((s) => s.half === "season")))}
            </span>
          </div>
          <HoldingsTable spot={spot} />
        </div>
      </div>
      <div className="spot-expanded-section">
        <h4 className="spot-expanded-heading">Why the money is where it is</h4>
        <ConditionsTable spot={spot} />
      </div>
      {spot.standingExits.length > 0 && (
        <div className="spot-expanded-section">
          <h4 className="spot-expanded-heading">Standing exits · retrying</h4>
          <ul className="spot-standing-exits">
            {spot.standingExits.map((exit) => (
              <li key={exit.coin}>
                {formatToken(exit.qty)} {exit.coin} · {String(exit.failures)} failed attempts · next
                try {formatWhen(exit.nextAttemptAt)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="spot-expanded-section">
        <h4 className="spot-expanded-heading">The slices</h4>
        <SlicesTable spot={spot} />
      </div>
      <div className="spot-expanded-footer">
        <PricesNote spot={spot} />
        <div className="spot-expanded-footer-fill" />
        {onOpenJournal && (
          <button type="button" className="button ghost small" onClick={onOpenJournal}>
            Open journal →
          </button>
        )}
      </div>
    </div>
  );
}
